import type { Control, FieldValues, Path } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

export type BillingDraftAddressFormValues = {
    companyName: string;
    companyAddress: string;
    companyCity: string;
    companyCountry: string;
    customerName: string;
    customerNumber: string;
    customerStreet: string;
    customerZipcode: string;
    customerCity: string;
    customerCountry: string;
};

type BillingDraftAddressFieldsProps<T extends FieldValues & BillingDraftAddressFormValues> = {
    control: Control<T>;
};

/**
 * "Rechnungssteller" and "Rechnungsempfänger" cards, bound to a react-hook-form form
 * that contains the billing draft address fields.
 */
export const BillingDraftAddressFields = <T extends FieldValues & BillingDraftAddressFormValues>({
    control,
}: BillingDraftAddressFieldsProps<T>) => {
    const renderField = (name: keyof BillingDraftAddressFormValues, label: string) => (
        <FormField
            control={control}
            name={name as Path<T>}
            render={({ field }) => (
                <FormItem>
                    <FormLabel>{label}</FormLabel>
                    <FormControl>
                        <Input {...field} />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            )}
        />
    );

    return (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            {/* Company Details */}
            <Card>
                <CardHeader className='pb-3'>
                    <CardTitle className='text-base'>Rechnungssteller</CardTitle>
                </CardHeader>
                <CardContent className='space-y-3'>
                    {renderField('companyName', 'Firmenname')}
                    {renderField('companyAddress', 'Adresse')}
                    {renderField('companyCity', 'PLZ / Stadt')}
                    {renderField('companyCountry', 'Land')}
                </CardContent>
            </Card>

            {/* Customer Details */}
            <Card>
                <CardHeader className='pb-3'>
                    <CardTitle className='text-base'>Rechnungsempfänger</CardTitle>
                </CardHeader>
                <CardContent className='space-y-3'>
                    {renderField('customerName', 'Name')}
                    {renderField('customerNumber', 'Kundennummer')}
                    {renderField('customerStreet', 'Straße')}
                    <div className='grid grid-cols-2 gap-3'>
                        {renderField('customerZipcode', 'PLZ')}
                        {renderField('customerCity', 'Stadt')}
                    </div>
                    {renderField('customerCountry', 'Land')}
                </CardContent>
            </Card>
        </div>
    );
};
